import {
  IconButton as MuiIconButton,
  TextField as MuiTextField,
  Typography
} from "@material-ui/core";
import React, { FC, useEffect, useState } from "react";
import styled from "@emotion/styled";
import moment from "moment";
import {
  Close as CloseIcon,
  Delete as DeleteIcon, Done as DoneIcon,
  Edit as EditIcon,
  RemoveRedEye as RemoveRedEyeIcon
} from "@material-ui/icons";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { Download } from "react-feather";
import {Thumbnails} from "../../../../../models/utils";
import {downloadImage} from "../../../../../utils/functions";

const Row = styled.tr`
  height: 72px;
  width: 100%;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1)
`

const NameCell = styled.td`
  text-align: start;
  vertical-align: middle;
`

const UpdateDateCell = styled.td`
  text-align: center;
  vertical-align: middle;
`

const MandatoryCell = styled.td`
  text-align: center;
  vertical-align: middle;
  min-width: 72px;
`

const SettingsCell = styled.td`
  text-align: end;
  vertical-align: middle;
  max-width: 173px;
`

const ImageWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: 22px;
`

const SettingsWrapper = styled.div`
  padding-right: 22px;
`

const IconButton = styled(MuiIconButton)`
  color: #78909C
`

const IconButtonEditMode = styled(MuiIconButton)`
  padding: 7px;
  background: #323c49;
  border-radius: 4px;
  margin-left: 10px;
  color: #78909C;
`

const TextField = styled(MuiTextField)`
  width: 100%;
  & .MuiOutlinedInput-input {
    padding: 12.5px 14px;
  }
`

const EditModeContainer = styled.div`
  width: 80%;
  display: flex;
  flex-direction: row;
  align-items: center;
`

type PropsType = {
  id: string
  image: string
  description: string
  thumbnails: Thumbnails
  updatedAt: string,
  isMandatoryImage: boolean,
  onEditUnitImage: (id: string, text: string) => void
  onDeleteImage: (id: string) => void
}

const ImagesTableRow: FC<PropsType> = ({
  id,
  image,
  description,
  thumbnails,
  updatedAt,
  isMandatoryImage,
  onEditUnitImage,
  onDeleteImage
}) => {
  const {t} = useTranslation()
  const [editMode, setEditMode] = useState(false)
  const [newDescription, setNewDescription] = useState('')

  useEffect(() => {
    if (editMode) {
      setNewDescription(description)
    }
  }, [editMode])

  const showPicture = (src: string) => {
    Swal.fire({
      imageUrl: src,
      imageWidth: 1000,
      background: '#233145',
      showConfirmButton: false,
      showCloseButton: true
    })
  }

  const deleteImage = () => {
    Swal.fire({
      title: t('imagesPage_Are_your_sure'),
      text: `${t('imagesPage_Delete')} ${image.split('/').pop()}?`,
      confirmButtonText: t('imagesPage_Yes'),
      cancelButtonText: t('imagesPage_Cancel'),
      showCancelButton: true,
      reverseButtons: true,
      width: '400px'
    }).then((result) => {
      if (result.isConfirmed) {
        onDeleteImage(id)
      }
    })
  }

  const handleSubmit = (id: string, description: string, newDescription: string) => {
    if (newDescription && newDescription !== description) {
      onEditUnitImage(id, newDescription)
    }
  }

  return (
    <Row>
      <NameCell>
        <ImageWrapper>
          <img
            src={thumbnails.small}
            alt=""
            width="52"
            onClick={() => showPicture(thumbnails.large)}
            style={{
              marginRight: '15px',
              cursor: 'pointer'
            }}
          />
          {editMode ? (
            <EditModeContainer>
              <TextField
                autoFocus
                type="edit"
                variant="outlined"
                value={newDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit(id, description, newDescription)
                    setEditMode(!editMode)
                  }
                  if (e.key === 'Escape') {
                    setEditMode(!editMode)
                  }
                }}
                onChange={(e) => (
                  setNewDescription(e.target.value)
                )}
                InputLabelProps={{shrink: false}}
              />
              <IconButtonEditMode
                title='Cancel'
                onClick={() => setEditMode(false)}
              >
                <CloseIcon/>
              </IconButtonEditMode>
              <IconButtonEditMode
                onClick={() => {
                  handleSubmit(id, description, newDescription)
                  setEditMode(!editMode)
                }}
              >
                <DoneIcon />
              </IconButtonEditMode>
            </EditModeContainer>
          ) : (
            <Typography>{description}</Typography>
          )}
        </ImageWrapper>
      </NameCell>
      <MandatoryCell>
        <Typography>{isMandatoryImage ? t('imagesPage_Mandatory') : ''}</Typography>
      </MandatoryCell>
      <UpdateDateCell>
        <Typography>{moment(updatedAt).format('MM/DD/YYYY')}</Typography>
      </UpdateDateCell>
      <SettingsCell>
        <SettingsWrapper>
          <IconButton
            title="Download image"
            aria-label="download"
            color="inherit"
            onClick={() => downloadImage(image)}
          >
            <Download />
          </IconButton>
          <IconButton
            title={t('imagesPage_Open_image')}
            aria-label="details"
            onClick={() => showPicture(thumbnails.large)}>
            <RemoveRedEyeIcon />
          </IconButton>
          <IconButton
            title={t('imagesPage_Edit')}
            aria-label="edit"
            style={{color: editMode ? '#4782da' : '#78909C'}}
            onClick={() => {
              setEditMode(!editMode)
            }}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={deleteImage}
            title={t('imagesPage_Delete_image')}
            aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </SettingsWrapper>
      </SettingsCell>
    </Row>
  )
}

export default ImagesTableRow
